import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Checkbox } from '@/components/ui/checkbox'
import { CheckedState } from '@/components/ui/form/form-checkbox'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import {
  $aiSettings,
  $textSettingsLoading,
  updateConvertToLowerCaseFx,
  updateGenerateEmojiFx,
  updateRemoveApostropheskFx,
  updateRemoveCommasFx,
  updateRemoveExclamationMarkFx,
  updateRemovePointsFx,
  updateRemoveQuestionMarkFx,
} from '@/modules/ai-features/models/ai-settings.ts'

export const TextSettings = () => {
  const aiSettings = useUnit($aiSettings)
  const isLoading = useUnit($textSettingsLoading)

  const labelClassName = `flex items-center gap-2 w-fit ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`

  const handleConvertToLowercaseChange = useCallback(
    (checked: CheckedState) => {
      if (typeof checked !== 'boolean') {
        return
      }

      updateConvertToLowerCaseFx(checked)
    },
    [],
  )

  const handleRemoveCommasChange = useCallback((checked: CheckedState) => {
    if (typeof checked !== 'boolean') {
      return
    }

    updateRemoveCommasFx(checked)
  }, [])

  const handleRemovePointsChange = useCallback((checked: CheckedState) => {
    if (typeof checked !== 'boolean') {
      return
    }

    updateRemovePointsFx(checked)
  }, [])

  const handleRemoveExclamationMarkChange = useCallback(
    (checked: CheckedState) => {
      if (typeof checked !== 'boolean') {
        return
      }

      updateRemoveExclamationMarkFx(checked)
    },
    [],
  )

  const handleRemoveQuestionMarkChange = useCallback(
    (checked: CheckedState) => {
      if (typeof checked !== 'boolean') {
        return
      }

      updateRemoveQuestionMarkFx(checked)
    },
    [],
  )

  const handleRemoveApostrophesChange = useCallback((checked: CheckedState) => {
    if (typeof checked !== 'boolean') {
      return
    }

    updateRemoveApostropheskFx(checked)
  }, [])

  const handleEmojiChange = useCallback((checked: CheckedState) => {
    if (typeof checked !== 'boolean') {
      return
    }

    updateGenerateEmojiFx(checked)
  }, [])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-medium">Punctuation and Text Settings</h2>
        <p className="text-sm text-secondary-300">
          With the settings below, you can control how and what the AI
          Translator uses or ignores when translating your input messages.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <label
          className={labelClassName}
          htmlFor="convertToLowercase"
        >
          <Checkbox
            id="convertToLowercase"
            checked={aiSettings?.convertToLowerCase}
            onCheckedChange={handleConvertToLowercaseChange}
            disabled={isLoading}
          />
          <span className="text-sm font-medium">Convert to lowercase</span>
        </label>
        <label
          className={labelClassName}
          htmlFor="removeCommas"
        >
          <Checkbox
            id="removeCommas"
            checked={aiSettings?.removeCommas}
            onCheckedChange={handleRemoveCommasChange}
            disabled={isLoading}
          />
          <span className="text-sm font-medium">Remove commas</span>
        </label>
        <label
          className={labelClassName}
          htmlFor="removePoints"
        >
          <Checkbox
            id="removePoints"
            checked={aiSettings?.removePoints}
            onCheckedChange={handleRemovePointsChange}
            disabled={isLoading}
          />
          <span className="text-sm font-medium">Remove points</span>
        </label>
        <label
          className={labelClassName}
          htmlFor="removeExclamationMark"
        >
          <Checkbox
            id="removeExclamationMark"
            checked={aiSettings?.removeExclamationMark}
            onCheckedChange={handleRemoveExclamationMarkChange}
            disabled={isLoading}
          />
          <span className="text-sm font-medium">Remove exclamation mark</span>
        </label>
        <label
          className={labelClassName}
          htmlFor="removeQuestionMark"
        >
          <Checkbox
            id="removeQuestionMark"
            checked={aiSettings?.removeQuestionMark}
            onCheckedChange={handleRemoveQuestionMarkChange}
            disabled={isLoading}
          />
          <span className="text-sm font-medium">Remove question mark</span>
        </label>
        <label
          className={labelClassName}
          htmlFor="removeApostrophes"
        >
          <Checkbox
            id="removeApostrophes"
            checked={aiSettings?.removeApostrophes}
            onCheckedChange={handleRemoveApostrophesChange}
            disabled={isLoading}
          />
          <span className="text-sm font-medium">Remove apostrophes</span>
        </label>
        <label
          className={labelClassName}
          htmlFor="generateEmoji"
        >
          <Checkbox
            id="generateEmoji"
            checked={aiSettings?.generateEmoji}
            onCheckedChange={handleEmojiChange}
            disabled={isLoading}
          />
          <span className="text-sm font-medium">Generate Emojis</span>
          <Tooltip content="Allows the AI Translator to use the emojis you've selected in Display Settings.">
            <InfoIcon className="text-secondary-300" />
          </Tooltip>
        </label>
      </div>
    </div>
  )
}
